import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Password mismatch detected');
      return;
    }

    setLoading(true);

    try {
      await register({
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
      });
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-tech-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 tech-grid opacity-30" />
      
      {/* Glow Effects */}
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-tech-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-tech-success/3 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-sm w-full">
        {/* Logo & Title */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded bg-tech-surface border border-tech-border mb-4 glow-accent-sm">
            <svg className="w-8 h-8 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <h1 className="text-2xl font-mono font-semibold text-tech-text tracking-tight">CREATE ACCOUNT</h1>
          <p className="text-tech-textMuted text-sm mt-1 font-mono">NEW USER REGISTRATION</p>
        </div>

        {/* Register Form */}
        <div className="tech-card p-6 animate-slide-right" style={{ animationDelay: '0.1s' }}>
          <div className="mb-5">
            <span className="tech-section-title">USER DATA</span>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-tech-danger/10 border border-tech-danger/30 rounded text-tech-danger text-sm font-mono flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-xs font-mono text-tech-textMuted mb-2">
                USERNAME *
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                className="tech-input"
                placeholder="choose username"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-mono text-tech-textMuted mb-2">
                EMAIL
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="tech-input"
                placeholder="user@domain.com"
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block text-xs font-mono text-tech-textMuted mb-2">
                FULL NAME
              </label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleChange}
                className="tech-input"
                placeholder="John Doe"
              />
            </div>

            <div className="tech-divider my-4" />

            <div>
              <label htmlFor="password" className="block text-xs font-mono text-tech-textMuted mb-2">
                PASSWORD *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="tech-input"
                placeholder="create password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-mono text-tech-textMuted mb-2">
                CONFIRM PASSWORD *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="tech-input"
                placeholder="confirm password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full tech-btn-primary py-3 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  INITIALIZING
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  CREATE ACCOUNT
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-tech-border">
            <p className="text-center text-sm text-tech-textMuted">
              Already registered?{' '}
              <Link to="/login" className="text-tech-accent hover:text-tech-accentHover transition">
                LOGIN
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-tech-textDim text-xs font-mono">SYS.v2.0.0 // SECURE CONNECTION</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
